import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { Grid, Container } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import GET_ORGANIZATION_AVAILABLE_LICENSES from 'graph/organization/get-organization-available-licenses';
import { Organization, OrganizationLicenseUsage, ProductType } from 'types/schema-generated-types';
import { getProductFamilyIds } from 'utilities/product-utils';
import { LicenseAssignFn, LicenseUnassignFn } from '../../../types';
import CreateClassConfirmation from '../CreateClassConfirmation';
import StepHeader from '../StepHeader';
import StudentLicenseAssignment from '../StudentLicenseAssignment';

export interface ConfirmationStepProps {
  classFormDisabled?: boolean;
  index: number;
  organizations: Organization[];
  onLicenseAssign: LicenseAssignFn;
  onLicenseUnassign: LicenseUnassignFn;
}

const ConfirmationStep = ({
  classFormDisabled = false,
  organizations,
  index,
  onLicenseAssign,
  onLicenseUnassign
}: ConfirmationStepProps) => {
  const { t } = useTranslation('ConfirmationStep');
  const classes = useStyles();

  const { data } = useQuery(GET_ORGANIZATION_AVAILABLE_LICENSES, {
    variables: { organizationId: organizations && organizations[0]?.id }
  });

  const organizationLicenseUsage: OrganizationLicenseUsage[] =
    data?.organization?.rootOrganization?.licenseUsage ?? [];

  const productFamilyIds = useMemo(
    () =>
      getProductFamilyIds(organizationLicenseUsage, {
        productType: ProductType.STUDENT,
        sortBy: 'product.family.name'
      }),
    [organizationLicenseUsage]
  );

  if (!data) return null;

  return (
    <div className={classes.root}>
      <StepHeader
        title={`${index}. ${t('Review & Confirm')}`}
        subtitle={t(
          'Please review your class information below and press CONFIRM to create your class.'
        )}
      />
      <div className={classes.mainGrid}>
        <div className={classes.classDetailsContainer}>
          <CreateClassConfirmation disabled={classFormDisabled} organizations={organizations} />
        </div>
      </div>
      <div className={classes.studentTableContainer}>
        <Grid container justify="center" alignItems="center">
          <Container>
            <StudentLicenseAssignment
              licenseUsage={organizationLicenseUsage}
              productFamilyIds={productFamilyIds}
              onLicenseAssign={onLicenseAssign}
              onLicenseUnassign={onLicenseUnassign}
            />
          </Container>
        </Grid>
      </div>
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  mainGrid: {
    height: '100%',
    backgroundColor: 'white',
    borderBottom: '1px solid #979797'
  },
  classDetailsContainer: {
    backgroundColor: 'white',
    maxWidth: '1280px',
    margin: 'auto'
  },
  studentTableContainer: {
    backgroundColor: 'white',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4)
  }
}));

export default ConfirmationStep;
